import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: ModalProps) {
  if (!isOpen) return null;
  return <>
      <div className="fixed inset-0 bg-navy-dark/80 backdrop-blur-sm z-50 transition-opacity" onClick={onClose} data-id="element-219" />
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-warm-white z-50 shadow-2xl flex flex-col rounded-xl overflow-hidden" data-id="element-220">
        <div className="flex items-center justify-between p-6 border-b border-slate-light/20 flex-shrink-0 bg-white" data-id="element-221">
          <h2 className="text-2xl font-heading font-bold text-navy" data-id="element-222">{title}</h2>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center text-slate hover:text-navy hover:bg-slate-light/10 rounded-full transition-colors" aria-label="Close" data-id="element-223">
            <XIcon className="w-5 h-5" data-id="element-224" />
          </button>
        </div>
        <div className="p-6 md:p-8 overflow-y-auto font-body text-text-dark/80 leading-relaxed text-sm md:text-base space-y-4 bg-warm-white" data-id="element-225">
          {children}
        </div>
      </div>
    </>;
}
export function Footer() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  return <>
      <footer className="bg-navy-dark py-12 px-4 text-center border-t border-white/10" data-id="element-226">
        <div className="max-w-4xl mx-auto" data-id="element-227">
          <p className="text-white font-body text-sm tracking-widest uppercase mb-6" data-id="element-228">
            © {new Date().getFullYear()} Diloré Publishing. All rights
            reserved.
          </p>

          <div className="flex items-center justify-center gap-6 mb-8" data-id="element-229">
            <button onClick={() => setPrivacyOpen(true)} className="text-white/80 font-body text-sm hover:text-white transition-colors cursor-pointer" data-id="element-230">
              Privacy Policy
            </button>
            <span className="text-white/50" data-id="element-231">|</span>
            <button onClick={() => setTermsOpen(true)} className="text-white/80 font-body text-sm hover:text-white transition-colors cursor-pointer" data-id="element-232">
              Terms of Service
            </button>
          </div>

          <p className="text-white/70 font-body text-xs max-w-2xl mx-auto leading-relaxed" data-id="element-233">
            This material is for informational purposes only and does not
            constitute legal or financial advice. Consult with a qualified
            professional regarding your specific situation.
          </p>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy" data-id="element-234">
        <p className="font-semibold text-navy" data-id="element-235">
          Effective Date: March 18, 2026
        </p>
        <p data-id="element-236">
          Diloré Publishing ("we," "us," or "our") respects your privacy and is
          committed to protecting the personal information you share with us.
          This Privacy Policy explains how we collect, use, and safeguard your
          information when you purchase or use The Inherited House Survival Kit.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-237">
          Information We Collect
        </h3>
        <p data-id="element-238">
          When you make a purchase, we collect only the information necessary to
          process your transaction, including your name, email address, and
          payment details. Payment information is processed securely through our
          third-party payment processor and is never stored on our servers.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-239">
          How We Use Your Information
        </h3>
        <p data-id="element-240">We use your information to:</p>
        <ul className="list-disc pl-6 space-y-1" data-id="element-241">
          <li data-id="element-242">Deliver your purchased digital product</li>
          <li data-id="element-243">Send purchase confirmation and download links</li>
          <li data-id="element-244">Respond to customer service inquiries</li>
          <li data-id="element-245">Comply with legal obligations</li>
        </ul>
        <p data-id="element-246">
          We do not sell, rent, or share your personal information with third
          parties for marketing purposes.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-247">
          Cookies & Tracking
        </h3>
        <p data-id="element-248">
          Our website may use essential cookies to ensure proper functionality.
          We do not use tracking cookies for advertising purposes.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-249">
          Data Security
        </h3>
        <p data-id="element-250">
          We implement industry-standard security measures to protect your
          personal information. However, no method of electronic transmission or
          storage is 100% secure, and we cannot guarantee absolute security.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-251">
          Your Rights
        </h3>
        <p data-id="element-252">
          You have the right to request access to, correction of, or deletion of
          your personal data. To exercise these rights, please reach out to us
          through the channels provided at the time of purchase.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-253">
          Changes to This Policy
        </h3>
        <p data-id="element-254">
          We may update this Privacy Policy from time to time. Any changes will
          be posted on this page with an updated effective date.
        </p>
      </LegalModal>

      {/* Terms of Service Modal */}
      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Terms of Service" data-id="element-255">
        <p className="font-semibold text-navy" data-id="element-256">
          Effective Date: March 18, 2026
        </p>
        <p data-id="element-257">
          By purchasing and using The Inherited House Survival Kit ("the
          Product"), you agree to the following terms and conditions set forth
          by Diloré Publishing ("we," "us," or "our").
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-258">
          Product Description
        </h3>
        <p data-id="element-259">
          The Inherited House Survival Kit is a digital informational product
          delivered as a downloadable PDF and checklist bundle. It is designed
          to provide general guidance for individuals navigating inherited
          property situations.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-260">
          Disclaimer
        </h3>
        <p data-id="element-261">
          The content provided in this product is for informational and
          educational purposes only. It does not constitute legal, financial,
          tax, or real estate advice. We strongly recommend consulting with
          qualified professionals (attorneys, CPAs, licensed real estate agents)
          regarding your specific situation. Results may vary based on
          individual circumstances, state laws, and other factors.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-262">
          Digital Product Delivery
        </h3>
        <p data-id="element-263">
          Upon successful payment, you will receive immediate access to download
          the Product. Due to the digital nature of this product, all sales are
          final. We do not offer refunds once the download link has been
          delivered.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-264">
          Intellectual Property
        </h3>
        <p data-id="element-265">
          All content within The Inherited House Survival Kit is the
          intellectual property of Diloré Publishing. You are granted a
          personal, non-transferable license to use the Product for your own
          purposes. You may not reproduce, distribute, resell, or share the
          Product or any portion of it without prior written consent.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-266">
          Limitation of Liability
        </h3>
        <p data-id="element-267">
          To the fullest extent permitted by law, Diloré Publishing shall not be
          liable for any direct, indirect, incidental, consequential, or special
          damages arising from your use of or inability to use the Product. Your
          use of the information provided is at your own risk.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-268">
          Governing Law
        </h3>
        <p data-id="element-269">
          These Terms of Service shall be governed by and construed in
          accordance with the laws of the United States, without regard to
          conflict of law principles.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-270">
          Changes to These Terms
        </h3>
        <p data-id="element-271">
          We reserve the right to modify these Terms of Service at any time.
          Updated terms will be posted on this page with a revised effective
          date. Continued use of the Product constitutes acceptance of the
          updated terms.
        </p>
      </LegalModal>
    </>;
}